import React, { useState } from "react";
import { motion } from "framer-motion";
import ProgressBarExp from "../../components/ProgressBarExp";

// Data untuk Teaching Methods Used (experience_id: 3)
const statements = [
  { id: 9, text: "I use direct instruction or lecture to explain key concepts." },
  { id: 10, text: "I use group discussions to encourage student participation." },
  { id: 11, text: "I use project-based learning in my classroom activities." },
  { id: 12, text: "I use inquiry-based or discovery learning methods." },
  { id: 13, text: "I use educational games to increase student engagement." },
  { id: 14, text: "I apply differentiated instruction to support diverse learners." },
];

const ratingOptions = [
  { value: 1, label: "Not at all" },
  { value: 2, label: "Rarely" },
  { value: 3, label: "Sometimes" },
  { value: 4, label: "Often" },
  { value: 5, label: "Always/Fully" },
];

const Exp3 = () => {
  const [selections, setSelections] = useState({});
  const [loading, setLoading] = useState(false);

  const student = JSON.parse(localStorage.getItem("student"));
  const studentId = student?.student_id;

  if (!studentId || Number(studentId) <= 0) {
    alert("Student session error or ID missing. Please log in again.");
    window.location.href = "/login";
    return;
  }

  const handleRatingSelect = (statementId, ratingValue) => {
    setSelections((prev) => ({
      ...prev,
      [statementId]: ratingValue,
    }));
  };

  const saveToDatabase = async () => {
    if (!studentId || Number(studentId) <= 0) {
      alert("Student session error or ID missing. Please log in again.");
      setLoading(false);
      window.location.href = "/login";
      return;
    }

    setLoading(true);

    try {
      const statementKeys = statements.map((s) => s.id);
      const answeredCount = Object.keys(selections).length;

      if (answeredCount !== statements.length) {
        throw new Error("Please rate all statements before proceeding.");
      }

      const experienceData = statementKeys.map((id) => ({
        item_id: id,
        rating: selections[id],
      }));

      const nextStep = "4";

      const body = {
        student_id: Number(studentId),
        experience_id: 3,
        data: experienceData,
        next_step: nextStep,
      };

      const res = await fetch(
        "http://localhost:3000/api/teaching-experience/save",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        alert(data.message || "Error saving data");
        setLoading(false);
        return;
      }

      const currentStudent = JSON.parse(localStorage.getItem("student"));
      const updatedStudent = {
        ...currentStudent,
        te_current_step: data.next_step || nextStep,
      };

      localStorage.setItem("student", JSON.stringify(updatedStudent));

      window.location.href = "/teaching-experience/exp4";
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to save. Try again.");
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (Object.keys(selections).length !== statements.length) {
      alert("Please rate all statements.");
      return;
    }
    saveToDatabase();
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-gradient-to-b from-blue-900 via-blue-700 to-blue-500 text-white px-4 overflow-hidden">
      <div className="w-full max-w-lg mt-6 z-10">
        <ProgressBarExp step={3} totalSteps={7} />
      </div>

      <div className="flex flex-col items-center justify-center gap-1 w-full max-w-5xl relative z-10 p-4">
        <h1 className="text-xl md:text-2xl font-bold text-yellow-300 leading-snug text-center drop-shadow-md mt-4">
          3. Teaching Methods Used
        </h1>
        <p className="text-sm md:text-base text-blue-200 mb-4">
          Rate how often you use each of the following teaching methods.
        </p>

        <div className="w-full flex flex-col gap-1 p-4 bg-blue-800/50 rounded-lg shadow-xl">
          {statements.map((st, index) => (
            <div
              key={st.id}
              className="flex flex-col md:flex-row items-start md:items-center gap-3 p-3 border-b border-blue-700 last:border-b-0"
            >
              <div className="w-full md:w-1/3 flex-shrink-0 text-sm md:text-base font-semibold text-white">
                {index + 1}. {st.text}
              </div>

              <div className="w-full md:w-2/3 flex justify-around gap-2 p-2">
                {ratingOptions.map((opt) => (
                  <motion.div
                    key={opt.value}
                    whileHover={{ scale: 1.05 }}
                    onClick={() => handleRatingSelect(st.id, opt.value)}
                    className={`flex flex-col items-center justify-center text-center cursor-pointer p-2 rounded-lg transition-all duration-200 ${
                      selections[st.id] === opt.value
                        ? "bg-yellow-400 text-blue-900 shadow-lg scale-105"
                        : "bg-blue-600/70 text-white hover:bg-blue-500"
                    } text-xs w-1/5`}
                    style={{ minWidth: "60px" }}
                  >
                    <span className="font-bold text-sm">{opt.value}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                    <span className="sm:hidden">{opt.label.split("/")[0]}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <motion.button
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleNext}
        disabled={loading}
        className="mt-1 mb-12 bg-yellow-300 text-blue-900 font-bold px-12 py-3 rounded-full shadow-lg disabled:bg-gray-400"
      >
        {loading ? "Saving..." : "Next →"}
      </motion.button>

      <p className="absolute bottom-3 text-blue-200 text-xs">
        © 2025 Universitas Terbuka
      </p>
    </div>
  );
};

export default Exp3;
